import type {
  ChatClient,
  ChatChannelSummary,
  ChatMessagesPage,
  ChatDirectoryPerson,
  ChatAskTurn,
  ChatAskImage,
  ChatAskResult,
} from '@ampersandbrands/ui';
import { apiGet, apiPost } from './api';
import { API_BASE } from './apiBase';

const CHAT = `${API_BASE}/v1/chat`;

// The directory is a full org list; fetch once and filter client-side.
let directoryCache: ChatDirectoryPerson[] | null = null;

// The API returns two shapes in `images`: candyology entries that are already
// presigned URLs, and content-attachment refs that need a follow-up resolve.
// The shared widget renders only ChatAskImage ({ url, caption }), so we
// normalize here before handing the result back.
type AskAmpWireImage =
  | { url: string; caption?: string | null }
  | { item_id: string; attachment_id: string; filename?: string | null; caption?: string | null };

interface AskAmpWireResponse {
  answer: string | null;
  images?: AskAmpWireImage[];
}

// Persisted Ask Amp exchange from GET /v1/chat/ask/history. `images` carries
// the same union the ask endpoint returns (presigned candyology entries plus
// content-attachment refs), so each item is normalized through the same
// resolver before it reaches the widget.
interface AskHistoryWireItem {
  id: number;
  question: string;
  answer: string | null;
  images?: AskAmpWireImage[];
  created_at: string;
}

interface AskHistoryWireResponse {
  items: AskHistoryWireItem[];
  next_cursor?: number | null;
}

async function resolveWireImage(img: AskAmpWireImage): Promise<ChatAskImage | null> {
  if ('url' in img && img.url) {
    return { url: img.url, caption: img.caption ?? null };
  }
  if ('item_id' in img && 'attachment_id' in img) {
    try {
      const r = await apiGet<{ url: string }>(
        `${API_BASE}/v1/content/published/${encodeURIComponent(img.item_id)}/attachments/${encodeURIComponent(img.attachment_id)}`,
      );
      if (!r?.url) return null;
      return { url: r.url, caption: img.caption ?? null };
    } catch {
      // Drop this image; a bad ref must not fail the whole answer.
      return null;
    }
  }
  return null;
}

// Normalizes a wire image list to the ChatAskImage shape the widget renders.
// Each ref resolves independently (resolveWireImage catches its own failure
// and yields null), so a bad ref is dropped without failing the rest.
async function resolveWireImages(wireImages: AskAmpWireImage[]): Promise<ChatAskImage[]> {
  const settled = await Promise.allSettled(wireImages.map(resolveWireImage));
  return settled
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter((img): img is ChatAskImage => img !== null);
}

// Implements the shared ChatWidget's data contract against Cockpit's api layer.
export function createChatClient(): ChatClient {
  return {
    async listChannels() {
      const r = await apiGet<{ channels: ChatChannelSummary[] }>(`${CHAT}/channels`);
      return r.channels ?? [];
    },
    async listMessages(channelKey, cursor) {
      const qs = cursor != null ? `?cursor=${encodeURIComponent(cursor)}` : '';
      return await apiGet<ChatMessagesPage>(
        `${CHAT}/channels/${encodeURIComponent(channelKey)}/messages${qs}`,
      );
    },
    async sendMessage(channelKey, input) {
      await apiPost(`${CHAT}/channels/${encodeURIComponent(channelKey)}/messages`, input);
    },
    async markRead(channelKey, lastReadId) {
      await apiPost(`${CHAT}/channels/${encodeURIComponent(channelKey)}/read`, {
        last_read_id: lastReadId,
      });
    },
    async searchDirectory(query) {
      if (!directoryCache) {
        const r = await apiGet<{ people: { name: string; email: string }[] }>(
          `${API_BASE}/v1/core/directory`,
        );
        directoryCache = (r.people ?? []).map((p) => ({ email: p.email, name: p.name }));
      }
      const q = query.trim().toLowerCase();
      return directoryCache
        .filter((p) => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
        .slice(0, 25);
    },
    async openDm(email, name) {
      const r = await apiPost<{ channel_key: string }>(`${CHAT}/dm`, { email, name });
      return r.channel_key;
    },
    async askAmp(
      question: string,
      history?: ChatAskTurn[],
      storeNo?: string,
    ): Promise<ChatAskResult> {
      const body: { question: string; history?: ChatAskTurn[]; store_no?: string } = { question };
      if (history && history.length > 0) body.history = history;
      // Scopes stock questions to a store. The api honors it for HQ callers;
      // store callers' own store_no wins server-side.
      if (storeNo) body.store_no = storeNo;
      const r = await apiPost<AskAmpWireResponse>(`${CHAT}/ask`, body);
      const answer = r.answer ?? null;
      const images = await resolveWireImages(r.images ?? []);
      return images.length > 0 ? { answer, images } : { answer };
    },
    async askHistory(cursor?: number) {
      const qs = cursor != null ? `?cursor=${encodeURIComponent(cursor)}` : '';
      const r = await apiGet<AskHistoryWireResponse>(`${CHAT}/ask/history${qs}`);
      const items = await Promise.all(
        (r.items ?? []).map(async (item) => {
          const images = await resolveWireImages(item.images ?? []);
          return {
            id: item.id,
            question: item.question,
            answer: item.answer ?? null,
            created_at: item.created_at,
            ...(images.length > 0 ? { images } : {}),
          };
        }),
      );
      return { items, next_cursor: r.next_cursor ?? null };
    },
  };
}
